import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  CssBaseline,
  Toolbar,
  ThemeProvider,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import TrendsComponent from "./AdminTrends";
import PostComponent from "./post/PostComponent";
import VerifyAccountsComponent from "./VerifyAccount";
import AllUserComponent from "./user_details/AllUsers";
import AdminProfileComponent from "./AdminProfile";
import DisableAccountComponent from "./DisableAccComponent";
import ChatComponent from "./chat/ChatComponent";
import SupportAgentComponent from "./support/SupportAgentComponent";
import ReportComponent from "./report/ReportComponent";
import FaqsComponent from "./FAQsComponent";
import DocumentComponent from "./documents/DocumentComponent";
import DeletionRequestsComponent from "./DeletionRequestsComponent";
import SystemMessagesComponent from "./SystemMessagesComponent";
import TestimonialsComponent from "./testimonials/TestimonialsComponent";
import VideoManagementComponent from "./VideoManagementComponent";
import GuidanceComponent from "./GuidanceComponent";
import HadithComponent from "./HadithComponent";
import Navbar from "./navbar/Navbar";
import Sidebar from "./sidebar/Sidebar";
import darkTheme from "./dashboardTheme";

const drawerWidth = 300;

// Sections are addressed as /dashboard/<key>; "trends" is the /dashboard home. Values are tab titles.
const SECTION_TITLES = {
  trends: "Dashboard",
  posts: "Posts",
  "verify-accounts": "Verify Accounts",
  "all-users": "All Users",
  profile: "Profile",
  "disable-account": "Account",
  "system-messages": "System Messages",
  support: "Support",
  chats: "Chat",
  reports: "Reports",
  "deletion-requests": "Deletion Requests",
  faqs: "FAQs",
  legal: "Documents",
  testimonials: "Testimonials",
  videos: "Videos",
  guidance: "Guidance",
  hadith: "Daily Hadith",
};

export default function AdminDashboard() {
  const { currentUser, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { section } = useParams();
  const navigate = useNavigate();
  const activeComponent = section || "trends";
  const setActiveComponent = useCallback(
    (name) => navigate(name === "trends" ? "/dashboard" : `/dashboard/${name}`),
    [navigate]
  );
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  // Each section is a fresh page; don't keep the previous section's scroll position.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeComponent]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${SECTION_TITLES[activeComponent] || "Dashboard"} — MJMarry Admin`;
    return () => {
      document.title = previousTitle;
    };
  }, [activeComponent]);

  const handleComponentChange = (componentName) => {
    setActiveComponent(componentName);
    if (mobileOpen) setMobileOpen(false);
  };

  if (!SECTION_TITLES[activeComponent]) return <Navigate to="/dashboard" replace />;

  // Render component based on activeComponent
  const renderComponent = () => {
    switch (activeComponent) {
      case "trends":
        return <TrendsComponent />;
      case "posts":
        return <PostComponent />;
      case "verify-accounts":
        return <VerifyAccountsComponent />;
      case "all-users":
        return <AllUserComponent />;
      case "profile":
        return <AdminProfileComponent />;
      case "disable-account":
        return <DisableAccountComponent />;
      case "system-messages":
        return <SystemMessagesComponent />;
      case "support":
        return <SupportAgentComponent setActiveComponent={setActiveComponent} />;
      case "chats":
        return <ChatComponent />;
      case "reports":
        return <ReportComponent />;
      case "deletion-requests":
        return <DeletionRequestsComponent />;
      case "faqs":
        return <FaqsComponent />;
      case "legal":
        return <DocumentComponent />;
      case "testimonials":
        return <TestimonialsComponent />;
      case "videos":
        return <VideoManagementComponent />;
      case "guidance":
        return <GuidanceComponent />;
      case "hadith":
        return <HadithComponent />;
      default:
        return <TrendsComponent />;
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ display: "flex" }}>
        {/* Navbar */}
        <Navbar
          drawerWidth={drawerWidth}
          onDrawerToggle={handleDrawerToggle}
          logout={logout}
          setActiveComponent={setActiveComponent}
        />

        {/* Sidebar */}
        <Sidebar
          drawerWidth={drawerWidth}
          mobileOpen={mobileOpen}
          onDrawerToggle={handleDrawerToggle}
          activeComponent={activeComponent}
          handleComponentChange={handleComponentChange}
          currentUser={currentUser}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // Without this the flex item grows to its widest table on phones and the overflow is clipped.
            minWidth: 0,
            mt: { xs: 6, sm: 6, md: 4 },
            pt: { sm: 6, md: 4 },
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            minHeight: "100vh",
            // Resolved inside the dashboard theme; useTheme() here returned the public theme's light background.
            bgcolor: "background.default",
          }}
        >
          {renderComponent()}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
